import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";
import { toast } from "sonner";
import { timeSlots, displayTime, todayISO, initials, ageDisplay } from "@/lib/format";
import { Users, Loader2, CheckCheck, Building2 } from "lucide-react";

export default function BookAppointmentDialog({ open, onOpenChange, patient, onBooked }) {
  const [date, setDate] = useState(todayISO());
  const [time, setTime] = useState("");
  const [mode, setMode] = useState("In-person");
  const [invite, setInvite] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  // Multi-facility
  const [facilities, setFacilities] = useState([]);
  const [facilityId, setFacilityId] = useState("");

  // Phase 3 — multi-doctor
  const [showCc, setShowCc] = useState(false);
  const [ccMembers, setCcMembers] = useState([]);
  const [ccLoading, setCcLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [commonSlots, setCommonSlots] = useState(null);
  const [findingSlots, setFindingSlots] = useState(false);

  const defaultSlots = useMemo(() => timeSlots(7, 22, 15), []);
  const slots = commonSlots !== null ? commonSlots : defaultSlots;

  // Load doctor's facilities (once per open)
  useEffect(() => {
    if (!open) return;
    api.get("/facilities").then(({ data }) => {
      const items = (data.items || []).filter((f) => f.active);
      setFacilities(items);
      const primary = items.find((f) => f.is_primary) || items[0];
      if (primary) setFacilityId(primary.id);
    }).catch(() => {});
  }, [open]);

  // Load care-circle members when the multi-doctor pane opens
  useEffect(() => {
    if (!showCc || ccMembers.length > 0) return;
    setCcLoading(true);
    api.get("/care-circle/members")
      .then(({ data }) => setCcMembers(data || []))
      .catch(() => toast.error("Could not load care-circle members"))
      .finally(() => setCcLoading(false));
  }, [showCc, ccMembers.length]);

  useEffect(() => { setCommonSlots(null); setTime(""); }, [selectedIds, date]);

  const toggleDoctor = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const findCommonSlots = async () => {
    setFindingSlots(true);
    try {
      const { data } = await api.get("/care-circle/common-slots", {
        params: { doctor_ids: selectedIds.join(","), date },
      });
      setCommonSlots(data.slots || []);
      if ((data.slots || []).length === 0) toast.info("No common free slot — try a different date");
      else toast.success(`${(data.slots || []).length} common slot(s) found`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not find common slots"); }
    finally { setFindingSlots(false); }
  };

  const doSubmit = async (force = false) => {
    if (!patient) return;
    if (!time) return toast.error("Pick a time slot");
    setBusy(true);
    try {
      await api.post("/appointments", {
        patient_id: patient.id, date, time, mode, send_invite: invite, notes,
        additional_doctor_ids: selectedIds,
        facility_id: facilityId || null,
        force,
      });
      toast.success(selectedIds.length ? `Appointment booked with ${selectedIds.length + 1} doctor(s)` : "Appointment booked");
      onBooked?.();
      onOpenChange(false);
      setTime(""); setNotes(""); setSelectedIds([]); setShowCc(false); setCommonSlots(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Cross-facility conflict — 409 FACILITY_CONFLICT
      if (err?.response?.status === 409 && detail?.code === "FACILITY_CONFLICT") {
        if (window.confirm(`${detail.message}\n\nBook anyway at both facilities?`)) {
          setBusy(false);
          return doSubmit(true);
        }
      } else {
        toast.error(typeof detail === "string" ? detail : (detail?.message || "Failed to book"));
      }
    } finally { setBusy(false); }
  };
  const submit = () => doSubmit(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-[24px] max-w-xl max-h-[90vh] overflow-y-auto" data-testid="book-appointment-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold tracking-tight text-[#0A4C36]" style={{ fontFamily: "Instrument Sans" }}>Book appointment</DialogTitle>
        </DialogHeader>
        {patient && (
          <div className="flex items-center gap-3 mt-2 mb-4">
            <div className="w-12 h-12 rounded-full bg-[#0A4C36]/8 text-[#0A4C36] flex items-center justify-center font-semibold">
              {initials(patient.name)}
            </div>
            <div>
              <div className="font-semibold text-[#1F2A24]">{patient.name}</div>
              <div className="text-xs text-[#8A9992]">{patient.gender} &middot; {ageDisplay(patient)}</div>
            </div>
          </div>
        )}
        <div className="space-y-5">
          {/* Facility picker — shown only if the doctor has ≥ 2 active facilities */}
          {facilities.length > 1 && (
            <div>
              <label className="field-label block mb-2"><Building2 size={12} className="inline mr-1" /> Facility</label>
              <select data-testid="appt-facility" value={facilityId} onChange={(e) => setFacilityId(e.target.value)} className="input-clean">
                {facilities.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}{f.is_primary ? " · Primary" : ""}{f.city ? ` · ${f.city}` : ""}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="field-label block mb-2">When is the appointment?</label>
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="appt-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input-clean" />
              <select data-testid="appt-time" value={time} onChange={(e) => setTime(e.target.value)} className="input-clean">
                <option value="">
                  {commonSlots !== null
                    ? (slots.length ? "Common time slot" : "No common slots")
                    : "Select time"}
                </option>
                {slots.map((s) => <option key={s} value={s}>{displayTime(s)}</option>)}
              </select>
            </div>
            {commonSlots !== null && (
              <div className="text-[11px] text-[#0A4C36] mt-1 inline-flex items-center gap-1">
                <CheckCheck size={12} /> Showing {slots.length} slot(s) where every selected doctor is free
                <button type="button" onClick={() => setCommonSlots(null)} className="ml-2 underline hover:text-[#073B29]">clear filter</button>
              </div>
            )}
          </div>

          {/* Phase 3 — Multi-doctor block */}
          <div className="rounded-2xl border border-[#EFECE5] bg-[#F7F4EC]/40 p-3" data-testid="multi-doctor-block">
            <button
              type="button"
              onClick={() => setShowCc((s) => !s)}
              className="w-full flex items-center justify-between text-left"
              data-testid="toggle-multi-doctor"
            >
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-[#1F2A24]">
                <Users size={14} className="text-[#0A4C36]" /> Invite colleagues from Care Circle
                {selectedIds.length > 0 && (
                  <span className="ml-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#0A4C36] text-white">{selectedIds.length}</span>
                )}
              </span>
              <span className="text-[11px] text-[#8A9992]">{showCc ? "Hide" : "Show"}</span>
            </button>

            {showCc && (
              <div className="mt-3 space-y-3" data-testid="multi-doctor-panel">
                {ccLoading ? (
                  <div className="text-xs text-[#8A9992] inline-flex items-center gap-2">
                    <Loader2 size={12} className="animate-spin" /> Loading care circle…
                  </div>
                ) : ccMembers.length === 0 ? (
                  <div className="text-xs text-[#B14A2A]">
                    No members in your Care Circle yet. Add colleagues from the <b>Care Circle</b> tab.
                  </div>
                ) : (
                  <>
                    <div className="max-h-40 overflow-y-auto space-y-1.5 pr-1">
                      {ccMembers.map((m) => (
                        <label key={m.id} className="flex items-center gap-3 py-1.5 cursor-pointer" data-testid={`multi-doc-${m.id}`}>
                          <input
                            type="checkbox"
                            checked={selectedIds.includes(m.id)}
                            onChange={() => toggleDoctor(m.id)}
                            className="w-4 h-4 accent-[#0A4C36]"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-[#1F2A24] truncate">{m.name}</div>
                            {(m.specialties || []).length > 0 && (
                              <div className="text-[10px] text-[#5C7568] truncate">{m.specialties.join(" · ")}</div>
                            )}
                          </div>
                        </label>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={findCommonSlots}
                      disabled={findingSlots || selectedIds.length === 0}
                      className="btn-outline-emerald text-xs"
                      data-testid="find-common-slots"
                    >
                      {findingSlots ? <Loader2 size={12} className="animate-spin" /> : <CheckCheck size={12} />}
                      Find common free slots on {date}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="field-label block mb-2">Consultation mode</label>
            <div className="flex gap-2">
              {["In-person", "Remote"].map((m) => (
                <button key={m} onClick={() => setMode(m)} data-testid={`appt-mode-${m.toLowerCase()}`}
                  className={`rounded-xl px-4 py-2 text-sm font-medium border ${mode === m ? "bg-[#0A4C36] text-white border-[#0A4C36]" : "bg-white border-[#E8E6E1]"}`}>{m}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="field-label block mb-2">Want to send an invite to the patient?</label>
            <div className="flex gap-2">
              <button onClick={() => setInvite(true)} data-testid="invite-yes" className={`rounded-xl px-5 py-2 text-sm font-semibold border ${invite ? "bg-[#0A4C36] text-white border-[#0A4C36]" : "bg-[#E6F0EC] border-[#E6F0EC] text-[#0A4C36]"}`}>Yes</button>
              <button onClick={() => setInvite(false)} data-testid="invite-no" className={`rounded-xl px-5 py-2 text-sm font-semibold border ${!invite ? "bg-[#0A4C36] text-white border-[#0A4C36]" : "bg-white border-[#E8E6E1]"}`}>No</button>
            </div>
          </div>
          <div>
            <label className="field-label block mb-2">Reason / notes</label>
            <input data-testid="appt-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="input-clean" placeholder="e.g. Follow-up on diabetes" />
          </div>
          <div className="flex justify-end pt-2">
            <button data-testid="appt-submit" disabled={busy} className="btn-emerald" onClick={submit}>
              {busy ? "Booking…" : "Book appointment"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
